// ─────────────────────────────────────────────────────────────────────────────
// DocumentsTabComponent
// ─────────────────────────────────────────────────────────────────────────────
// Displays the Knowledge Base documents tab content.
// Responsibilities:
//   - Render stats bar, toolbar (search + filter + download), type-filter pills
//   - Render document table with loading, empty, and populated states
//   - Dispatch store actions for load, filter, search, and sort
// ─────────────────────────────────────────────────────────────────────────────

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { DATE_TIME_FORMAT } from 'src/app/config/global.config';
import { CommonService } from 'src/app/shared/common.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiCheckboxChange } from 'src/app/shared/models/selection.models';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { UiModule } from 'src/app/ui/ui.module';
import * as aiChatActions from 'src/app/features/ai-chat/store/ai-chat.actions';

import { IKbDocument, IKbStats, IKbTypeFilter, KbDocumentType } from '../../models/knowledge-base.models';
import * as KbActions from '../../store/knowledge-base.actions';
import * as KbSelectors from '../../store/knowledge-base.selectors';
import { FileRowComponent } from './file-row/file-row.component';
import { KbConfirmationDialogComponent } from '../kb-confirmation-dialog/kb-confirmation-dialog.component';
import { UploadDocumentDialogComponent } from './upload-document-dialog/upload-document-dialog.component';

@Component({
  selector: 'app-documents-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    SvgIconComponent,
    FileRowComponent,
    UiModule,
    SharedModule,
  ],
  templateUrl: './documents-tab.component.html',
  styleUrls: ['./documents-tab.component.scss'],
})
export class DocumentsTabComponent implements OnInit, OnDestroy {
  // ── Refs ─────────────────────────────────────────────────────────────

  @ViewChild('typeFiltersScroll', { static: false }) typeFiltersScrollRef!: ElementRef<HTMLElement>;

  // ── Store observables ──────────────────────────────────────────────────

  filteredDocuments$!: Observable<IKbDocument[]>;
  stats$!: Observable<IKbStats>;
  typeFilters$!: Observable<IKbTypeFilter[]>;
  activeTypeFilter$!: Observable<KbDocumentType | 'all'>;
  loading$!: Observable<{ documents: boolean; upload: boolean }>;

  // ── Local state ────────────────────────────────────────────────────────

  searchQuery = '';
  activeSortKey = 'upload_date';
  activeSortMode: 'asc' | 'desc' = 'desc';

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IKbDocument, string>((doc) => doc.id);
  downloadLoading = false;

  // ── Global config ───────────────────────────────────────────────────────

  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  // ── Drag-to-scroll state ──────────────────────────────────────────────

  private isDraggingFilters = false;
  private dragStartX = 0;
  private dragScrollLeft = 0;
  private dragMoved = false;
  private boundOnFiltersMouseMove = this.onFiltersScrollMouseMove.bind(this);
  private boundOnFiltersMouseUp = this.onFiltersScrollMouseUp.bind(this);

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private dialog: MatDialog,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(KbActions.LoadDocuments());

    this.filteredDocuments$ = this.store.select(KbSelectors.selectFilteredDocuments);
    this.filteredDocuments$.pipe(takeUntil(this.destroy$)).subscribe((docs) => this.selection.setRows(docs));
    this.stats$ = this.store.select(KbSelectors.selectStats);
    this.typeFilters$ = this.store.select(KbSelectors.selectTypeFilters);
    this.activeTypeFilter$ = this.store.select(KbSelectors.selectActiveTypeFilter);
    this.loading$ = this.store.select(KbSelectors.selectLoading);

    // The query itself lives in the store, but the field bound to the input is
    // local. This tab is created and destroyed on tab switches, so without
    // seeding it the input would come back empty while the list stayed filtered.
    this.store
      .select(KbSelectors.selectSearchQuery)
      .pipe(take(1))
      .subscribe((query) => (this.searchQuery = query));

    this.store
      .select(KbSelectors.selectSortKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((key) => (this.activeSortKey = key));
    this.store
      .select(KbSelectors.selectSortMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => (this.activeSortMode = mode));

    this.searchInput$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((query) => {
      this.store.dispatch(KbActions.SetSearchQuery({ query }));
    });

    this.actions$.pipe(ofType(KbActions.DownloadDocumentsSuccess, KbActions.DownloadDocumentsError), takeUntil(this.destroy$)).subscribe(() => {
      this.downloadLoading = false;
    });

    this.actions$.pipe(ofType(KbActions.DeleteDocumentsSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('mousemove', this.boundOnFiltersMouseMove);
    document.removeEventListener('mouseup', this.boundOnFiltersMouseUp);
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  onSearchChange(query: string): void {
    this.searchInput$.next(query);
  }

  onTypeFilterChange(filterType: KbDocumentType | 'all'): void {
    if (this.dragMoved) return;
    this.store.dispatch(KbActions.SetTypeFilter({ filterType }));
  }

  onUploadFile(): void {
    this.dialog.open(UploadDocumentDialogComponent, {
      width: '900px',
      disableClose: false,
    });
  }

  onAskAi(): void {
    this.store.dispatch(aiChatActions.ToggleAiChatDrawer());
  }

  onDownloadSelected(): void {
    // The bulk bar only renders with a selection, but the guard stays: it is the
    // only thing standing between a programmatic call and an empty download.
    if (!this.selection.hasSelection) {
      this.commonService.showToast(this.translate.instant('knowledge_base.toasts.select_at_least_one'), 'WARNING');
      return;
    }
    this.downloadLoading = true;
    this.store.dispatch(KbActions.DownloadDocuments({ documentIds: this.selection.ids }));
  }

  onDeleteSelected(): void {
    const documentIds = this.selection.ids;
    if (!documentIds.length) return;

    this.dialog.open(KbConfirmationDialogComponent, {
      data: { action: 'delete' as const, documentIds },
      disableClose: true,
    });
  }

  onToggleSelectDocument(documentId: string, event: UiCheckboxChange): void {
    this.selection.toggleOne(documentId, event.checked, event);
  }

  onToggleSelectAll(checked: boolean): void {
    this.selection.toggleAll(checked);
  }

  onSortChange(sortKey: string, sortMode: 'asc' | 'desc'): void {
    this.store.dispatch(KbActions.SetSort({ sortKey, sortMode }));
  }

  trackByDocumentId(_index: number, document: IKbDocument): string {
    return document.id;
  }

  // ── Drag-to-scroll for type filter pills ──────────────────────────────

  onFiltersScrollMouseDown(event: MouseEvent): void {
    const el = this.typeFiltersScrollRef?.nativeElement;
    if (!el) return;

    this.isDraggingFilters = true;
    this.dragMoved = false;
    this.dragStartX = event.pageX;
    this.dragScrollLeft = el.scrollLeft;
    el.classList.add('knowledge-base__type-filters-scroll--dragging');

    document.addEventListener('mousemove', this.boundOnFiltersMouseMove);
    document.addEventListener('mouseup', this.boundOnFiltersMouseUp);
  }

  private onFiltersScrollMouseMove(event: MouseEvent): void {
    if (!this.isDraggingFilters) return;
    event.preventDefault();
    const el = this.typeFiltersScrollRef.nativeElement;
    const dx = event.pageX - this.dragStartX;
    if (Math.abs(dx) > 3) this.dragMoved = true;
    el.scrollLeft = this.dragScrollLeft - dx;
  }

  private onFiltersScrollMouseUp(): void {
    if (!this.isDraggingFilters) return;
    this.isDraggingFilters = false;
    const el = this.typeFiltersScrollRef?.nativeElement;
    el?.classList.remove('knowledge-base__type-filters-scroll--dragging');

    document.removeEventListener('mousemove', this.boundOnFiltersMouseMove);
    document.removeEventListener('mouseup', this.boundOnFiltersMouseUp);
  }
}
